import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import theme from '../theme.config';
const BrandLogo = theme.assets.logo;

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email dan password harus diisi');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Selamat datang, ${user.name}!`);
      
      if (redirectTo) {
        navigate(redirectTo);
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'reseller') {
        navigate('/reseller/dashboard');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Login gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-4 cyber-grid">
      <div className="w-full max-w-md">
        {/* HUD Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <img src={BrandLogo} alt={theme.brand.name} className="h-14 w-auto object-contain" />
            </div>
          </div>
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            <span className="font-mono text-[10px] text-primary tracking-widest uppercase">SECURE_CHANNEL · AUTH</span>
          </div>
          <h1 className="font-bold text-2xl text-white uppercase tracking-wider">
            Masuk ke Akun
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            Identifikasi diri untuk mengakses command center
          </p>
        </div>

        {/* Cyber card */}
        <form onSubmit={handleSubmit}
          className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/50 rounded-tl-xl" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-secondary/50 rounded-br-xl" />

          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
                ▸ Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="operator@aethercore.id"
                className="bg-black/50 border-border text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
                ▸ Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="bg-black/50 border-border text-white pr-10 font-mono focus:border-primary focus:ring-1 focus:ring-primary/30"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 neon-green transition-all duration-300"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />
                  Authenticating...
                </span>
              ) : (
                <span className="font-mono">[ MASUK ]</span>
              )}
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground text-sm font-mono">
              Belum terdaftar?{' '}
              <Link to="/register" className="text-primary hover:text-primary/80 underline underline-offset-2">
                Daftar akun baru
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
